import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Icon,
  Progress,
  Select,
  SimpleGrid,
  Stat,
  StatArrow,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react'
import {
  differenceInDays,
  endOfMonth,
  format,
  isWithinInterval,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarCheck, DollarSign, Percent, Users } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { supabase } from '../../lib/supabase' // Adjusted import path

interface Property {
  id: string
  title: string
  occupancy_rate: number
  monthly_revenue: number
  total_nights: number
  occupied_nights: number
  combined_score: number
}

interface Metrics {
  totalReservations: number
  reservationsByStatus: { [key: string]: number }
  avgReservationValue: number
  upcomingCheckIns: number
  totalClients: number
  newClients: number
  occupancyRate: number
  revenueGrowth: number
  recentReservations: any[]
  monthlyRevenue: number
  propertyMetrics: Property[]
  dailyRevenue: { date: string; revenue: number }[]
}

export function InsightsPage() {
  // Renamed component
  const [metrics, setMetrics] = useState<Metrics>({
    totalReservations: 0,
    reservationsByStatus: {},
    avgReservationValue: 0,
    upcomingCheckIns: 0,
    totalClients: 0,
    newClients: 0,
    occupancyRate: 0,
    revenueGrowth: 0,
    recentReservations: [],
    monthlyRevenue: 0,
    propertyMetrics: [],
    dailyRevenue: [],
  })
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(startOfMonth(new Date()))
  const [endDate, setEndDate] = useState(endOfMonth(new Date()))
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('all')
  const [properties, setProperties] = useState<{ id: string; title: string }[]>(
    []
  )
  const toast = useToast()

  const fetchProperties = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title')
        .order('title')

      if (error) throw error
      setProperties(data || [])
    } catch (error: any) {
      toast({
        title: 'Error fetching properties',
        description: error.message,
        status: 'error',
        duration: 5000,
      })
    }
  }, [toast])

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true)
      const today = new Date()
      const thirtyDaysAgo = subMonths(today, 1)
      const nextWeek = new Date(today)
      nextWeek.setDate(nextWeek.getDate() + 7)
      const totalDays = differenceInDays(endDate, startDate) + 1

      // Build base query for reservations
      let reservationsQuery = supabase
        .from('reservations')
        .select('*')
        .gte('check_in_date', startDate.toISOString().split('T')[0])
        .lte('check_out_date', endDate.toISOString().split('T')[0])

      // Add property filter if specific property is selected
      if (selectedPropertyId !== 'all') {
        reservationsQuery = reservationsQuery.eq(
          'property_id',
          selectedPropertyId
        )
      }

      // Build base queries
      let propertiesQuery = supabase.from('properties').select('*')
      let upcomingQuery = supabase
        .from('reservations')
        .select('*')
        .gte('check_in_date', today.toISOString().split('T')[0])
        .lte('check_in_date', nextWeek.toISOString().split('T')[0])
      let lastMonthQuery = supabase
        .from('reservations')
        .select('reserve_total')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .lt('created_at', today.toISOString())
      let thisMonthQuery = supabase
        .from('reservations')
        .select('reserve_total')
        .gte('created_at', today.toISOString())
      let recentReservationsQuery = supabase
        .from('reservations')
        .select(
          `
          *,
          property:properties(title),
          client:clients(name)
        `
        )
        .order('created_at', { ascending: false })
        .limit(5)

      // Add property filters if specific property is selected
      if (selectedPropertyId !== 'all') {
        propertiesQuery = propertiesQuery.eq('id', selectedPropertyId)
        upcomingQuery = upcomingQuery.eq('property_id', selectedPropertyId)
        lastMonthQuery = lastMonthQuery.eq('property_id', selectedPropertyId)
        thisMonthQuery = thisMonthQuery.eq('property_id', selectedPropertyId)
        recentReservationsQuery = recentReservationsQuery.eq(
          'property_id',
          selectedPropertyId
        )
      }

      // Fetch all required data in parallel
      const [
        propertiesResult,
        reservationsResult,
        clientsResult,
        upcomingResult,
        newClientsResult,
        lastMonthReservationsResult,
        thisMonthReservationsResult,
        recentReservationsResult,
      ] = await Promise.all([
        propertiesQuery,
        reservationsQuery,
        supabase.from('clients').select('*'),
        upcomingQuery,
        supabase
          .from('clients')
          .select('*')
          .gte('created_at', thirtyDaysAgo.toISOString()),
        lastMonthQuery,
        thisMonthQuery,
        recentReservationsQuery,
      ])

      if (reservationsResult.error) throw reservationsResult.error
      const reservations = reservationsResult.data || []
      const properties = propertiesResult.data || []

      // Calculate status counts with translated labels
      const statusCounts = reservations.reduce(
        (acc: { [key: string]: number }, curr) => {
          const translatedStatus = getReservationStatusLabel(curr.status)
          acc[translatedStatus] = (acc[translatedStatus] || 0) + 1
          return acc
        },
        {}
      )

      // Calculate average reservation value
      const totalPrice = reservations.reduce(
        (sum, r) => sum + Number(r.reserve_total),
        0
      )
      const avgPrice =
        reservations.length > 0 ? totalPrice / reservations.length : 0

      // Calculate revenue growth
      const lastMonthRevenue = (lastMonthReservationsResult.data || []).reduce(
        (sum, r) => sum + Number(r.reserve_total),
        0
      )
      const thisMonthRevenue = (thisMonthReservationsResult.data || []).reduce(
        (sum, r) => sum + Number(r.reserve_total),
        0
      )
      const revenueGrowth =
        lastMonthRevenue > 0
          ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
          : 0

      // Calculate property metrics
      const propertyMetrics = properties.map((property) => {
        const propertyReservations = reservations.filter(
          (r) => r.property_id === property.id
        )

        const occupiedNights = propertyReservations.reduce(
          (sum, reservation) => {
            const checkIn = new Date(reservation.check_in_date)
            const checkOut = new Date(reservation.check_out_date)
            const nights = differenceInDays(checkOut, checkIn)
            return sum + nights
          },
          0
        )

        const monthlyRevenue = propertyReservations.reduce(
          (sum, r) => sum + Number(r.reserve_total),
          0
        )

        const occupancyRate = (occupiedNights / totalDays) * 100

        // Calculate combined score (occupancy rate * revenue)
        // This gives higher weight to properties that are both well-occupied and generating good revenue
        const combinedScore = (occupancyRate / 100) * monthlyRevenue

        return {
          id: property.id,
          title: property.title,
          occupancy_rate: occupancyRate,
          monthly_revenue: monthlyRevenue,
          total_nights: totalDays,
          occupied_nights: occupiedNights,
          combined_score: combinedScore,
        }
      })

      // Sort properties by combined score
      propertyMetrics.sort((a, b) => b.combined_score - a.combined_score)

      // Calculate daily revenue
      const dailyRevenue: { date: string; revenue: number }[] = []
      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        const dayRevenue = reservations
          .filter((r) => {
            const checkIn = parseISO(r.check_in_date)
            const checkOut = parseISO(r.check_out_date)
            return isWithinInterval(currentDate, {
              start: checkIn,
              end: checkOut,
            })
          })
          .reduce((sum, r) => {
            const nights = differenceInDays(
              parseISO(r.check_out_date),
              parseISO(r.check_in_date)
            )
            return sum + Number(r.reserve_total) / nights
          }, 0)

        dailyRevenue.push({
          date: format(currentDate, 'dd/MM'),
          revenue: dayRevenue,
        })

        currentDate.setDate(currentDate.getDate() + 1)
      }

      // Calculate overall metrics
      const totalRevenue = propertyMetrics.reduce(
        (sum, p) => sum + p.monthly_revenue,
        0
      )

      const overallOccupancyRate =
        (propertyMetrics.reduce((sum, p) => sum + p.occupied_nights, 0) /
          (propertyMetrics.length * totalDays)) *
        100

      setMetrics({
        totalReservations: reservations.length,
        reservationsByStatus: statusCounts,
        avgReservationValue: avgPrice,
        upcomingCheckIns: (upcomingResult.data || []).length,
        totalClients: (clientsResult.data || []).length,
        newClients: (newClientsResult.data || []).length,
        occupancyRate: overallOccupancyRate,
        revenueGrowth,
        recentReservations: recentReservationsResult.data || [],
        monthlyRevenue: totalRevenue,
        propertyMetrics,
        dailyRevenue,
      })
    } catch (error: any) {
      toast({
        title: 'Error fetching metrics',
        description: error.message,
        status: 'error',
        duration: 5000,
      })
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, selectedPropertyId, toast])

  useEffect(() => {
    fetchProperties()
  }, [fetchProperties])

  useEffect(() => {
    fetchMetrics()
  }, [fetchMetrics])

  // Função para converter status de reserva para português
  const getReservationStatusLabel = (status: string) => {
    switch (status) {
      case 'waiting_checkin':
        return 'Aguardando check-in'
      case 'guest_in_house':
        return 'Hóspede na casa'
      case 'checkout_done':
        return 'Check-out feito'
      default:
        return status
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  const getOccupancyStatus = (rate: number) => {
    if (rate >= 70) return { color: 'green', label: 'Ótima' }
    if (rate >= 40) return { color: 'yellow', label: 'Regular' }
    return { color: 'red', label: 'Baixa' }
  }

  if (loading) {
    return (
      <Box p={8}>
        <Progress size="xs" isIndeterminate />
      </Box>
    )
  }

  return (
    <Box p={8}>
      <HStack justify="space-between" mb={8}>
        <Box>
          <Heading size="lg" mb={2}>
            Performance Metrics
          </Heading>
          <Text color="gray.600">Análise de desempenho das propriedades</Text>
        </Box>
        <HStack spacing={4}>
          <Select
            value={selectedPropertyId}
            onChange={(e) => setSelectedPropertyId(e.target.value)}
            width="200px"
          >
            <option value="all">Todas as Propriedades</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.title}
              </option>
            ))}
          </Select>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date || startOfMonth(new Date()))}
            dateFormat="dd/MM/yyyy"
            customInput={
              <Button variant="outline" size="sm">
                {format(startDate, 'dd/MM/yyyy')}
              </Button>
            }
            locale={ptBR}
          />
          <Text>até</Text>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date || endOfMonth(new Date()))}
            dateFormat="dd/MM/yyyy"
            customInput={
              <Button variant="outline" size="sm">
                {format(endDate, 'dd/MM/yyyy')}
              </Button>
            }
            locale={ptBR}
          />
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Stat>
              <Flex justify="space-between" align="flex-start">
                <Box flex="1">
                  <Text color="gray.500" fontSize="sm" fontWeight="medium">
                    Receita Total
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" mt={2}>
                    {formatCurrency(metrics.monthlyRevenue)}
                  </Text>
                  <HStack spacing={2} mt={2}>
                    <StatArrow
                      type={
                        metrics.revenueGrowth >= 0 ? 'increase' : 'decrease'
                      }
                      color={
                        metrics.revenueGrowth >= 0 ? 'green.500' : 'red.500'
                      }
                    />
                    <Text fontSize="sm" color="gray.500">
                      {Math.abs(metrics.revenueGrowth).toFixed(1)}% do mês
                      anterior
                    </Text>
                  </HStack>
                </Box>
                <Box p={2} bg="blue.50" borderRadius="lg" color="blue.500">
                  <Icon as={DollarSign} boxSize={6} />
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <Flex justify="space-between" align="flex-start">
                <Box flex="1">
                  <Text color="gray.500" fontSize="sm" fontWeight="medium">
                    Total de Reservas
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" mt={2}>
                    {metrics.totalReservations}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {metrics.upcomingCheckIns} check-ins próximos
                  </Text>
                </Box>
                <Box p={2} bg="orange.50" borderRadius="lg" color="orange.500">
                  <Icon as={CalendarCheck} boxSize={6} />
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <Flex justify="space-between" align="flex-start">
                <Box flex="1">
                  <Text color="gray.500" fontSize="sm" fontWeight="medium">
                    Total de Clientes
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" mt={2}>
                    {metrics.totalClients}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {metrics.newClients} novos em 30 dias
                  </Text>
                </Box>
                <Box p={2} bg="purple.50" borderRadius="lg" color="purple.500">
                  <Icon as={Users} boxSize={6} />
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <Flex justify="space-between" align="flex-start">
                <Box flex="1">
                  <Text color="gray.500" fontSize="sm" fontWeight="medium">
                    Taxa de Ocupação
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" mt={2}>
                    {formatPercent(metrics.occupancyRate)}
                  </Text>
                  <Badge
                    colorScheme={
                      getOccupancyStatus(metrics.occupancyRate).color
                    }
                    mt={2}
                    px={3}
                    py={1}
                    borderRadius="lg"
                    textTransform="none"
                  >
                    {getOccupancyStatus(metrics.occupancyRate).label}
                  </Badge>
                </Box>
                <Box p={2} bg="green.50" borderRadius="lg" color="green.500">
                  <Icon as={Percent} boxSize={6} />
                </Box>
              </Flex>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Heading size="sm" mb={6}>
              Reservas por Status
            </Heading>
            <Box height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={Object.entries(metrics.reservationsByStatus).map(
                    ([status, count]) => ({
                      status,
                      count,
                    })
                  )}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3182CE" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Heading size="sm" mb={6}>
              Receita Diária
            </Heading>
            <Box height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metrics.dailyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#3182CE"
                    name="Receita"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
        <Card>
          <CardBody>
            <Heading size="sm" mb={6}>
              Top 5 Propriedades
            </Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Propriedade</Th>
                  <Th isNumeric>Ocupação</Th>
                  <Th isNumeric>Receita</Th>
                </Tr>
              </Thead>
              <Tbody>
                {metrics.propertyMetrics.slice(0, 5).map((property) => (
                  <Tr key={property.id}>
                    <Td>{property.title}</Td>
                    <Td isNumeric>
                      <Badge
                        colorScheme={
                          getOccupancyStatus(property.occupancy_rate).color
                        }
                        px={3}
                        py={1}
                        borderRadius="lg"
                        textTransform="none"
                      >
                        {formatPercent(property.occupancy_rate)}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      {formatCurrency(property.monthly_revenue)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Heading size="sm" mb={6}>
              5 Propriedades com Menor Desempenho
            </Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Propriedade</Th>
                  <Th isNumeric>Ocupação</Th>
                  <Th isNumeric>Receita</Th>
                </Tr>
              </Thead>
              <Tbody>
                {metrics.propertyMetrics
                  .slice(-5)
                  .reverse()
                  .map((property) => (
                    <Tr key={property.id}>
                      <Td>{property.title}</Td>
                      <Td isNumeric>
                        <Badge
                          colorScheme={
                            getOccupancyStatus(property.occupancy_rate).color
                          }
                          px={3}
                          py={1}
                          borderRadius="lg"
                          textTransform="none"
                        >
                          {formatPercent(property.occupancy_rate)}
                        </Badge>
                      </Td>
                      <Td isNumeric>
                        {formatCurrency(property.monthly_revenue)}
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card>
        <CardBody>
          <Heading size="sm" mb={6}>
            Todas as Propriedades
          </Heading>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Propriedade</Th>
                <Th isNumeric>Taxa de Ocupação</Th>
                <Th isNumeric>Noites Ocupadas</Th>
                <Th isNumeric>Noites Totais</Th>
                <Th isNumeric>Receita</Th>
              </Tr>
            </Thead>
            <Tbody>
              {metrics.propertyMetrics.map((property) => (
                <Tr key={property.id}>
                  <Td>{property.title}</Td>
                  <Td isNumeric>
                    <Badge
                      colorScheme={
                        getOccupancyStatus(property.occupancy_rate).color
                      }
                      px={3}
                      py={1}
                      borderRadius="lg"
                      textTransform="none"
                    >
                      {formatPercent(property.occupancy_rate)}
                    </Badge>
                  </Td>
                  <Td isNumeric>{property.occupied_nights}</Td>
                  <Td isNumeric>{property.total_nights}</Td>
                  <Td isNumeric>{formatCurrency(property.monthly_revenue)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    </Box>
  )
}
